import { useMemo, useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { persistAppendOnlyVersion, persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly'
import { formatCurrency, gerarCodigo, nowISO } from '../utils'
import type { Aprovacao, Cotacao, ItemCotacao, ItemRequisicao } from '../types'

const STATUS_COLOR: Record<string, string> = {
  ABERTA: '#60a5fa',
  EM_ANALISE: '#fbbf24',
  CONCLUIDA: '#4ade80',
  CANCELADA: '#6b7280',
}

type IdentifiableRecord = {
  _id?: string
  id?: string
  [key: string]: unknown
}

type ProposalFormValue = {
  valor_unitario: string
  prazo_entrega_dias: string
}

function refs(record: IdentifiableRecord | undefined) {
  return [record?.id, record?._id].filter(Boolean).map(String)
}

function sameRef(left: string | undefined, right: string | undefined, records: IdentifiableRecord[] = []) {
  if (!left || !right) return false
  if (left === right) return true

  return records.some(record => {
    const recordRefs = refs(record)
    return recordRefs.includes(left) && recordRefs.includes(right)
  })
}

function proposalKey(itemRequisicaoId: string, fornecedorId: string) {
  return `${itemRequisicaoId}::${fornecedorId}`
}

function itemValorUnitario(item: ItemCotacao) {
  return Number(item.valor_unitario_cotado ?? item.valor_unitario ?? 0)
}

function itemValorTotal(item: ItemCotacao) {
  return Number(item.valor_total_cotado ?? item.valor_total ?? 0)
}

function itemVencedor(item: ItemCotacao) {
  return Boolean(item.fornecedor_selecionado ?? item.vencedor)
}

export default function Cotacoes() {
  const {
    cotacoes, setCotacoes, itensCotacao, setItensCotacao, requisicoes, setRequisicoes,
    itensRequisicao, fornecedores, aprovacoes, setAprovacoes, currentUser, addLog,
  } = useApp()
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [winnerCotacao, setWinnerCotacao] = useState<Cotacao | null>(null)
  const [winnerFornecedorId, setWinnerFornecedorId] = useState('')
  const [winnerMotivo, setWinnerMotivo] = useState('Melhor proposta no comparativo')
  const [saving, setSaving] = useState(false)
  const [choosingWinner, setChoosingWinner] = useState(false)
  const [toast, setToast] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [itensPage, setItensPage] = useState(1)
  const [itensPageSize, setItensPageSize] = useState(10)
  const [proposalValues, setProposalValues] = useState<Record<string, ProposalFormValue>>({})
  const [form, setForm] = useState({
    requisicao_id: '',
    data_abertura: new Date().toISOString().slice(0, 10),
    data_fechamento: '',
    fornecedor_ids: ['', '', ''],
    observacoes: '',
  })

  const reqAprovadas = requisicoes.filter(r => ['APROVADA', 'EM_COTACAO'].includes(r.status) && !r.bloqueada_por_orcamento)
  const fornecedoresSelecionados = form.fornecedor_ids.filter(Boolean)
  const paginatedCotacoes = paginateItems(cotacoes, page, pageSize)
  const paginatedItensCotacao = paginateItems(itensCotacao, itensPage, itensPageSize)
  const requisicaoSelecionada = requisicoes.find(r => sameRef(r.id, form.requisicao_id, requisicoes as unknown as IdentifiableRecord[]))
  const itensForm = useMemo(
    () => itensRequisicao.filter(i => sameRef(i.requisicao_id, form.requisicao_id, requisicoes as unknown as IdentifiableRecord[])),
    [form.requisicao_id, itensRequisicao, requisicoes],
  )

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  function updateFornecedor(index: number, fornecedorId: string) {
    setForm(prev => ({
      ...prev,
      fornecedor_ids: prev.fornecedor_ids.map((id, i) => i === index ? fornecedorId : id),
    }))
  }

  function updateProposal(itemRequisicaoId: string, fornecedorId: string, field: keyof ProposalFormValue, value: string) {
    setProposalValues(prev => ({
      ...prev,
      [proposalKey(itemRequisicaoId, fornecedorId)]: {
        valor_unitario: prev[proposalKey(itemRequisicaoId, fornecedorId)]?.valor_unitario ?? '',
        prazo_entrega_dias: prev[proposalKey(itemRequisicaoId, fornecedorId)]?.prazo_entrega_dias ?? '',
        [field]: value,
      },
    }))
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.requisicao_id) e.requisicao_id = 'Requisicao obrigatoria'
    if (!form.data_fechamento) e.data_fechamento = 'Data de fechamento obrigatoria'
    if (new Set(fornecedoresSelecionados).size !== 3) e.fornecedores = 'Informe tres fornecedores diferentes para comparar.'
    if (fornecedoresSelecionados.length !== new Set(fornecedoresSelecionados).size) e.fornecedores = 'Nao repita o mesmo fornecedor.'
    if (requisicaoSelecionada?.bloqueada_por_orcamento) e.requisicao_id = 'Requisicao bloqueada por orcamento.'
    if (itensForm.length === 0) e.requisicao_id = 'Requisicao sem itens.'

    const missingProposal = itensForm.some(item =>
      fornecedoresSelecionados.some(fornecedorId => {
        const values = proposalValues[proposalKey(item.id, fornecedorId)]
        return !values || Number(values.valor_unitario) <= 0 || Number(values.prazo_entrega_dias) <= 0
      }),
    )
    if (itensForm.length > 0 && fornecedoresSelecionados.length === 3 && missingProposal) {
      e.propostas = 'Preencha valor unitario e prazo de entrega para cada item e fornecedor.'
    }
    return e
  }

  function resetForm() {
    setEditId(null)
    setForm({ requisicao_id: '', data_abertura: new Date().toISOString().slice(0, 10), data_fechamento: '', fornecedor_ids: ['', '', ''], observacoes: '' })
    setProposalValues({})
    setErrors({})
  }

  function openNew() {
    resetForm()
    setModal(true)
  }

  function openEdit(cotacao: Cotacao) {
    if (cotacao.status !== 'ABERTA') {
      showToast('Somente cotacoes abertas podem ter propostas editadas.')
      return
    }
    const fornecedoresCotacao = fornecedoresDaCotacao(cotacao.id)
    const valores: Record<string, ProposalFormValue> = {}
    itensCotacao
      .filter(item => sameRef(item.cotacao_id, cotacao.id, [cotacao as unknown as IdentifiableRecord]))
      .forEach(item => {
        valores[proposalKey(item.item_requisicao_id, item.fornecedor_id)] = {
          valor_unitario: String(itemValorUnitario(item) || ''),
          prazo_entrega_dias: String(item.prazo_entrega_dias || ''),
        }
      })
    setEditId(cotacao.id)
    setForm({
      requisicao_id: cotacao.requisicao_id,
      data_abertura: cotacao.data_abertura,
      data_fechamento: cotacao.data_fechamento,
      fornecedor_ids: [fornecedoresCotacao[0] || '', fornecedoresCotacao[1] || '', fornecedoresCotacao[2] || ''],
      observacoes: cotacao.observacoes || '',
    })
    setProposalValues(valores)
    setErrors({})
    setModal(true)
  }

  async function save() {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }

    setSaving(true)
    const cotacaoAtual = editId ? cotacoes.find(item => item.id === editId) : null
    const cotId = cotacaoAtual?.id || 'cot' + Date.now()
    const propostasAntigas = itensCotacao.filter(item => sameRef(item.cotacao_id, cotId, [cotacaoAtual as unknown as IdentifiableRecord]))
    const novasPropostas: ItemCotacao[] = []

    itensForm.forEach((item, itemIndex) => {
      fornecedoresSelecionados.forEach((fornecedorId, fornecedorIndex) => {
        const values = proposalValues[proposalKey(item.id, fornecedorId)]
        const valorUnitario = Number(Number(values.valor_unitario).toFixed(2))
        const valorTotal = Number((valorUnitario * item.quantidade).toFixed(2))
        novasPropostas.push({
          id: `ic${Date.now()}${itemIndex}${fornecedorIndex}`,
          cotacao_id: cotId,
          item_requisicao_id: item.id,
          fornecedor_id: fornecedorId,
          quantidade: item.quantidade,
          valor_unitario: valorUnitario,
          valor_total: valorTotal,
          valor_unitario_cotado: valorUnitario,
          valor_total_cotado: valorTotal,
          prazo_entrega_dias: Number(values.prazo_entrega_dias) || 0,
          vencedor: false,
          fornecedor_selecionado: false,
          createdAt: nowISO(),
        })
      })
    })

    const cotacaoSalva: Cotacao = {
      ...(cotacaoAtual ?? {}),
      id: cotId,
      requisicao_id: form.requisicao_id,
      numero_cotacao: cotacaoAtual?.numero_cotacao || gerarCodigo(),
      data_abertura: form.data_abertura,
      data_fechamento: form.data_fechamento,
      status: 'ABERTA',
      fornecedor_vencedor_id: undefined,
      motivo_escolha: undefined,
      observacoes: form.observacoes,
      createdAt: cotacaoAtual?.createdAt || nowISO(),
    }
    const requisicaoEmCotacao = !editId && requisicaoSelecionada
      ? { ...requisicaoSelecionada, status: 'EM_COTACAO' as const }
      : null

    try {
      if (propostasAntigas.length) {
        await persistDeletedVersions('itens_cotacao', propostasAntigas as unknown as Record<string, unknown>[])
      }
      await persistAppendOnlyVersion('cotacoes', cotacaoSalva as unknown as Record<string, unknown>)
      await Promise.all(novasPropostas.map(item => persistAppendOnlyVersion('itens_cotacao', item as unknown as Record<string, unknown>)))
      if (requisicaoEmCotacao) {
        await persistAppendOnlyVersion('requisicoes', requisicaoEmCotacao as unknown as Record<string, unknown>)
      }
    } catch (error) {
      console.error('Falha ao confirmar cotacao no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
      setSaving(false)
      return
    }

    setCotacoes(prev => editId ? prev.map(item => item.id === editId ? cotacaoSalva : item) : [...prev, cotacaoSalva])
    setItensCotacao(prev => [...prev.filter(item => !sameRef(item.cotacao_id, cotId, [cotacaoSalva as unknown as IdentifiableRecord])), ...novasPropostas])
    if (requisicaoEmCotacao) {
      setRequisicoes(prev => prev.map(r => r.id === form.requisicao_id ? requisicaoEmCotacao : r))
    }
    addLog('COTACAO', cotacaoSalva.id, editId ? 'EDITADO' : 'CRIADO', `Cotacao ${cotacaoSalva.numero_cotacao} ${editId ? 'atualizada' : 'criada'} com propostas manuais de ${fornecedoresSelecionados.length} fornecedores`)
    showToast(editId ? 'Cotacao atualizada.' : 'Cotacao criada com propostas por fornecedor.')
    setModal(false)
    setEditId(null)
    setSaving(false)
  }

  async function deleteCotacao(cotacao: Cotacao) {
    const confirmed = window.confirm(`Excluir a cotacao ${cotacao.numero_cotacao}? Esta acao remove tambem propostas e aprovacoes vinculadas.`)
    if (!confirmed) return

    try {
      const propostasVinculadas = itensCotacao.filter(item => sameRef(item.cotacao_id, cotacao.id, [cotacao as unknown as IdentifiableRecord]))
      const aprovacoesVinculadas = aprovacoes.filter(item => item.entidade_tipo === 'COTACAO' && item.entidade_id === cotacao.id)
      await persistDeletedVersions('itens_cotacao', propostasVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersions('aprovacoes', aprovacoesVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersion('cotacoes', cotacao as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setCotacoes(prev => prev.filter(item => item.id !== cotacao.id))
    setItensCotacao(prev => prev.filter(item => !sameRef(item.cotacao_id, cotacao.id, [cotacao as unknown as IdentifiableRecord])))
    setAprovacoes(prev => prev.filter(item => !(item.entidade_tipo === 'COTACAO' && item.entidade_id === cotacao.id)))
    addLog('COTACAO', cotacao.id, 'CANCELADO', `Cotacao ${cotacao.numero_cotacao} excluida`)
    showToast('Cotacao excluida.')
  }

  function totalFornecedor(cotacaoId: string, fornecedorId: string) {
    return itensCotacao
      .filter(i => sameRef(i.cotacao_id, cotacaoId) && i.fornecedor_id === fornecedorId)
      .reduce((s, item) => s + itemValorTotal(item), 0)
  }

  function prazoFornecedor(cotacaoId: string, fornecedorId: string) {
    const prazos = itensCotacao
      .filter(i => sameRef(i.cotacao_id, cotacaoId) && i.fornecedor_id === fornecedorId)
      .map(item => item.prazo_entrega_dias)
      .filter(Boolean)
    return prazos.length ? Math.max(...prazos) : 0
  }

  function fornecedoresDaCotacao(cotacaoId: string) {
    return Array.from(new Set(itensCotacao.filter(i => sameRef(i.cotacao_id, cotacaoId)).map(i => i.fornecedor_id)))
  }

  function openWinner(c: Cotacao) {
    const fornecedoresCot = fornecedoresDaCotacao(c.id)
    if (fornecedoresCot.length === 0) {
      showToast('Cotacao sem propostas.')
      return
    }
    const menorValor = fornecedoresCot
      .map(id => ({ id, total: totalFornecedor(c.id, id) }))
      .sort((a, b) => a.total - b.total)[0]?.id ?? ''
    setWinnerCotacao(c)
    setWinnerFornecedorId(c.fornecedor_vencedor_id || menorValor)
    setWinnerMotivo(c.motivo_escolha || 'Melhor proposta no comparativo')
  }

  async function escolherVencedorManual() {
    if (!winnerCotacao || !winnerFornecedorId) return
    const propostasCotacao = itensCotacao.filter(item => sameRef(item.cotacao_id, winnerCotacao.id, [winnerCotacao as unknown as IdentifiableRecord]))
    const propostasVencedoras = propostasCotacao.filter(item => item.fornecedor_id === winnerFornecedorId)
    if (propostasVencedoras.length === 0 || propostasVencedoras.some(item => itemValorTotal(item) <= 0)) {
      showToast('Fornecedor vencedor sem propostas validas.')
      return
    }

    const cotacaoAtualizada: Cotacao = {
      ...winnerCotacao,
      status: 'EM_ANALISE',
      fornecedor_vencedor_id: winnerFornecedorId,
      motivo_escolha: winnerMotivo.trim() || 'Fornecedor eleito no comparativo',
    }
    const propostasAtualizadas = propostasCotacao.map(item => ({
      ...item,
      vencedor: item.fornecedor_id === winnerFornecedorId,
      fornecedor_selecionado: item.fornecedor_id === winnerFornecedorId,
      valor_unitario_cotado: itemValorUnitario(item),
      valor_total_cotado: itemValorTotal(item),
    }))
    const jaExisteAprovacao = aprovacoes.some(a => a.entidade_tipo === 'COTACAO' && a.entidade_id === winnerCotacao.id && a.status === 'PENDENTE')
    const novaAprovacao: Aprovacao | null = jaExisteAprovacao ? null : {
      id: 'ap' + Date.now(),
      entidade_tipo: 'COTACAO',
      entidade_id: winnerCotacao.id,
      etapa_aprovacao: 'SUPRIMENTOS',
      aprovador_nome: '',
      perfil_aprovador: 'SUPRIMENTOS',
      solicitante_usuario_id: currentUser?.id,
      solicitante_nome: currentUser?.nome,
      solicitante_perfil: currentUser?.perfil,
      status: 'PENDENTE',
      createdAt: nowISO(),
    }

    setChoosingWinner(true)
    try {
      await persistAppendOnlyVersion('cotacoes', cotacaoAtualizada as unknown as Record<string, unknown>)
      await Promise.all(propostasAtualizadas.map(item => persistAppendOnlyVersion('itens_cotacao', item as unknown as Record<string, unknown>)))
      if (novaAprovacao) {
        await persistAppendOnlyVersion('aprovacoes', novaAprovacao as unknown as Record<string, unknown>)
      }
    } catch (error) {
      console.error('Falha ao confirmar vencedor da cotacao no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
      setChoosingWinner(false)
      return
    }

    setItensCotacao(prev => prev.map(item => {
      const atualizada = propostasAtualizadas.find(proposta => proposta.id === item.id)
      return atualizada ?? item
    }))
    setCotacoes(prev => prev.map(x => x.id === winnerCotacao.id ? cotacaoAtualizada : x))
    if (novaAprovacao) setAprovacoes(prev => [novaAprovacao, ...prev])
    addLog('COTACAO', winnerCotacao.id, 'EDITADO', `Fornecedor vencedor eleito: ${fornName(winnerFornecedorId)}`)
    showToast('Fornecedor vencedor eleito e cotacao enviada para aprovacao.')
    setWinnerCotacao(null)
    setChoosingWinner(false)
  }

  async function concluirSeAprovada(c: Cotacao) {
    const rejeitada = aprovacoes.some(a => a.entidade_tipo === 'COTACAO' && a.entidade_id === c.id && a.status === 'REJEITADO')
    const aprovada = aprovacoes.some(a => a.entidade_tipo === 'COTACAO' && a.entidade_id === c.id && a.status === 'APROVADO')
    if (rejeitada) {
      const cancelada: Cotacao = { ...c, status: 'CANCELADA' }
      await persistAppendOnlyVersion('cotacoes', cancelada as unknown as Record<string, unknown>)
      setCotacoes(prev => prev.map(x => x.id === c.id ? cancelada : x))
      showToast('Cotacao rejeitada no workflow.')
      return
    }
    if (!aprovada) {
      showToast('Aguardando aprovacao de Suprimentos.')
      return
    }
    const concluida: Cotacao = { ...c, status: 'CONCLUIDA' }
    try {
      await persistAppendOnlyVersion('cotacoes', concluida as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setCotacoes(prev => prev.map(x => x.id === c.id ? concluida : x))
    showToast('Cotacao concluida e liberada para OC.')
  }

  const reqName = (id: string) => requisicoes.find(r => sameRef(r.id, id, requisicoes as unknown as IdentifiableRecord[]))?.numero_requisicao || '-'
  const fornName = (id?: string) => fornecedores.find(f => f.id === id)?.nome_fantasia || '-'
  const itemReqName = (id: string) => itensRequisicao.find(i => i.id === id)?.descricao || '-'

  function renderProposalInputs(item: ItemRequisicao, fornecedorId: string) {
    const key = proposalKey(item.id, fornecedorId)
    const values = proposalValues[key] ?? { valor_unitario: '', prazo_entrega_dias: '' }
    const valorTotal = (Number(values.valor_unitario) || 0) * item.quantidade
    return (
      <tr key={key} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <td style={{ padding: '7px 8px', color: '#f7f8f8' }}>{item.descricao}</td>
        <td style={{ padding: '7px 8px', color: '#a0a4ab' }}>{fornName(fornecedorId)}</td>
        <td style={{ padding: '7px 8px', color: '#a0a4ab' }}>{item.quantidade}</td>
        <td style={{ padding: '7px 8px' }}>
          <input
            className="input-field proposal-value-input"
            type="number"
            min="0"
            step="0.01"
            value={values.valor_unitario}
            onChange={e => updateProposal(item.id, fornecedorId, 'valor_unitario', e.target.value)}
          />
        </td>
        <td style={{ padding: '7px 8px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(valorTotal)}</td>
        <td style={{ padding: '7px 8px' }}>
          <input
            className="input-field proposal-deadline-input"
            type="number"
            min="1"
            step="1"
            value={values.prazo_entrega_dias}
            onChange={e => updateProposal(item.id, fornecedorId, 'prazo_entrega_dias', e.target.value)}
          />
        </td>
      </tr>
    )
  }

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Cotacoes</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>{cotacoes.length} cotacoes</p>
        </div>
        <button onClick={openNew} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>+ Nova Cotacao</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {['Numero', 'Requisicao', 'Fornecedores', 'Vencedor', 'Menor valor', 'Status', 'Acoes'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {paginatedCotacoes.items.map(c => {
            const fornecedoresCot = fornecedoresDaCotacao(c.id)
            const menor = fornecedoresCot.length ? Math.min(...fornecedoresCot.map(id => totalFornecedor(c.id, id))) : 0
            return (
              <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                <td style={{ padding: '10px 12px', color: '#ffb4b1', fontFamily: 'monospace', fontSize: 11 }}>{c.numero_cotacao}</td>
                <td style={{ padding: '10px 12px', color: '#f7f8f8' }}>{reqName(c.requisicao_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{fornecedoresCot.length} fornecedor(es)</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{fornName(c.fornecedor_vencedor_id)}</td>
                <td style={{ padding: '10px 12px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(menor)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: (STATUS_COLOR[c.status] || '#94a3b8') + '22', color: STATUS_COLOR[c.status] || '#94a3b8', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{c.status.replace(/_/g, ' ')}</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {c.status === 'ABERTA' && <button onClick={() => openEdit(c)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>}
                  {c.status === 'ABERTA' && <button onClick={() => openWinner(c)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Eleger vencedor</button>}
                  {c.status === 'EM_ANALISE' && <button onClick={() => void concluirSeAprovada(c)} style={{ marginLeft: 6, background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Verificar aprovacao</button>}
                  <button onClick={() => void deleteCotacao(c)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer', marginLeft: 6 }}>Excluir</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <PaginationControls
        page={paginatedCotacoes.safePage}
        pageSize={pageSize}
        totalItems={cotacoes.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <div style={{ marginTop: 18 }} className="card p-4">
        <p style={{ color: '#8a8f98', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Comparativo de propostas</p>
        {itensCotacao.length === 0 ? <p style={{ color: '#62666d', fontSize: 13 }}>Nenhuma proposta registrada.</p> : (
          <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Cotacao', 'Item', 'Fornecedor', 'Qtd.', 'Vlr. unit.', 'Total', 'Prazo', 'Vencedor'].map(h => <th key={h} style={{ color: '#62666d', textAlign: 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
            <tbody>{paginatedItensCotacao.items.map((i, index) => {
              const cot = cotacoes.find(c => sameRef(c.id, i.cotacao_id, cotacoes as unknown as IdentifiableRecord[]))
              return <tr key={`${i.id}-${index}`} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '6px 8px', color: '#ffb4b1' }}>{cot?.numero_cotacao || '-'}</td>
                <td style={{ padding: '6px 8px', color: '#f7f8f8' }}>{itemReqName(i.item_requisicao_id)}</td>
                <td style={{ padding: '6px 8px', color: '#a0a4ab' }}>{fornName(i.fornecedor_id)}</td>
                <td style={{ padding: '6px 8px', color: '#a0a4ab' }}>{i.quantidade}</td>
                <td style={{ padding: '6px 8px', color: '#a0a4ab' }}>{formatCurrency(itemValorUnitario(i))}</td>
                <td style={{ padding: '6px 8px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(itemValorTotal(i))}</td>
                <td style={{ padding: '6px 8px', color: '#a0a4ab' }}>{i.prazo_entrega_dias} dias</td>
                <td style={{ padding: '6px 8px', color: itemVencedor(i) ? '#4ade80' : '#62666d' }}>{itemVencedor(i) ? 'sim' : '-'}</td>
              </tr>
            })}</tbody>
          </table>
          <PaginationControls
            page={paginatedItensCotacao.safePage}
            pageSize={itensPageSize}
            totalItems={itensCotacao.length}
            onPageChange={setItensPage}
            onPageSizeChange={setItensPageSize}
          />
        </>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(240,64,59,0.35)', borderRadius: 10, padding: 24, width: 920, maxHeight: '88vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 18px' }}>{editId ? 'Editar Cotacao' : 'Nova Cotacao'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="label-field">Requisicao aprovada *</label>
                <select className="input-field" value={form.requisicao_id} onChange={e => {
                  setForm(p => ({ ...p, requisicao_id: e.target.value }))
                  setProposalValues({})
                }}>
                  <option value="">Selecione...</option>
                  {reqAprovadas.map(r => <option key={r.id} value={r.id}>{r.numero_requisicao} - {r.justificativa.slice(0, 50)}</option>)}
                </select>
                {errors.requisicao_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.requisicao_id}</p>}
              </div>
              <div>
                <label className="label-field">Abertura</label>
                <input className="input-field" type="date" value={form.data_abertura} onChange={e => setForm(p => ({ ...p, data_abertura: e.target.value }))} />
              </div>
              <div>
                <label className="label-field">Fechamento *</label>
                <input className="input-field" type="date" value={form.data_fechamento} onChange={e => setForm(p => ({ ...p, data_fechamento: e.target.value }))} />
                {errors.data_fechamento && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.data_fechamento}</p>}
              </div>
              {[0, 1, 2].map(index => (
                <div key={index} style={{ gridColumn: index === 2 ? 'span 2' : undefined }}>
                  <label className="label-field">Fornecedor {index + 1}</label>
                  <select className="input-field" value={form.fornecedor_ids[index]} onChange={e => updateFornecedor(index, e.target.value)}>
                    <option value="">Selecione...</option>
                    {fornecedores.filter(f => f.status === 'ATIVO').map(f => <option key={f.id} value={f.id}>{f.nome_fantasia}</option>)}
                  </select>
                </div>
              ))}
              {errors.fornecedores && <p style={{ gridColumn: 'span 2', color: '#f87171', fontSize: 11 }}>{errors.fornecedores}</p>}
            </div>

            {itensForm.length > 0 && fornecedoresSelecionados.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <p style={{ color: '#f7f8f8', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>Propostas dos fornecedores</p>
                {errors.propostas && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.propostas}</p>}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>{['Item', 'Fornecedor', 'Qtd.', 'Vlr. unit.', 'Total', 'Prazo dias'].map(h => <th key={h} style={{ color: '#62666d', textAlign: 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {itensForm.flatMap(item => fornecedoresSelecionados.map(fornecedorId => renderProposalInputs(item, fornecedorId)))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 18 }}>
              <label className="label-field">Observacoes</label>
              <textarea className="input-field" rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} className="btn-ghost" disabled={saving}>Cancelar</button>
              <button onClick={() => void save()} className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar alteracoes' : 'Abrir Cotacao'}</button>
            </div>
          </div>
        </div>
      )}

      {winnerCotacao && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(240,64,59,0.35)', borderRadius: 10, padding: 24, width: 640 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 18px' }}>Eleger vencedor</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
              <thead><tr>{['Fornecedor', 'Total', 'Prazo max.', 'Selecionar'].map(h => <th key={h} style={{ color: '#62666d', textAlign: 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
              <tbody>
                {fornecedoresDaCotacao(winnerCotacao.id).map(fornecedorId => (
                  <tr key={fornecedorId} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '8px', color: '#f7f8f8' }}>{fornName(fornecedorId)}</td>
                    <td style={{ padding: '8px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(totalFornecedor(winnerCotacao.id, fornecedorId))}</td>
                    <td style={{ padding: '8px', color: '#a0a4ab' }}>{prazoFornecedor(winnerCotacao.id, fornecedorId)} dias</td>
                    <td style={{ padding: '8px' }}>
                      <input type="radio" checked={winnerFornecedorId === fornecedorId} onChange={() => setWinnerFornecedorId(fornecedorId)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <label className="label-field">Motivo da escolha</label>
            <textarea className="input-field" rows={2} value={winnerMotivo} onChange={e => setWinnerMotivo(e.target.value)} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setWinnerCotacao(null)} className="btn-ghost" disabled={choosingWinner}>Cancelar</button>
              <button onClick={() => void escolherVencedorManual()} className="btn-primary" disabled={choosingWinner}>{choosingWinner ? 'Confirmando...' : 'Confirmar vencedor'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

